import { useCallback, useEffect, useState } from 'react';
import {
  TCreateCardHolderProps,
  useCreateCardHolder,
} from '@xhype/card-app-sdk';
import { useGetCardHolderData, useGetUserData } from '@package/smart-wallet';
import * as Sentry from '@sentry/react';

/* Types */
export type TUserData = {
  cardTypeId: string;
  areaCode: string;
  mobile: string;
  firstName: string;
  lastName: string;
  cardHolderModel?: 'B2B' | 'B2C';
  birthday?: string;
  country?: string;
  town?: string;
  address?: string;
  postCode?: string;
  nationality?: string;
  gender?: 'M' | 'F';
  occupation?: string;
  annualSalary?: string;
  accountPurpose?: string;
  expectedMonthlyVolume?: string;
  // B2C identity document fields (required when cardHolderModel = 'B2C')
  idType?: 'PASSPORT' | 'HK_HKID' | 'DLN' | 'GOVERNMENT_ISSUED_ID_CARD';
  idNumber?: string;
  issueDate?: string;
  idNoExpiryDate?: string;
  idFrontId?: string;
  idBackId?: string;
  idHoldId?: string;
};

export type TCreateCardHolderHookProps = {
  onError?: (error: any) => void;
  onSuccess?: () => void;
};

/* Hook */
export const useCreateUserCardHolder = ({
  onError,
  onSuccess,
}: TCreateCardHolderHookProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [userData, setUserData] = useState<undefined | TUserData>(undefined);
  const { userInfo, refetch: refetchUserInfo } = useGetUserData();
  const { refetch: refetchCardHolderInfo } = useGetCardHolderData();

  /* Mutations */
  const createCardHolder = useCreateCardHolder({
    onError: (error: any) => {
      console.log(`Create Card Holder: Unexpected error - ${error}`);
      setError(error?.response?.data?.msg);
      onError?.(error);
    },
    onSuccess: () => {
      console.log('Create Card Holder: Successfully created');
      refetchUserInfo();
      refetchCardHolderInfo();
      onSuccess?.();
    },
  });

  /* Callbacks */
  const register = useCallback(async (props: TUserData) => {
    setError(undefined);
    setUserData(props);
  }, []);

  const createNewCardHolder = useCallback(async () => {
    if (!userData || !userInfo?.id) return;
    setIsLoading(true);

    try {
      const cardHolderData = {
        userId: userInfo.id,
        cardTypeId: Number(userData.cardTypeId),
        address: userData.address,
        areaCode: userData.areaCode,
        mobile: userData.mobile,
        firstName: userData.firstName,
        lastName: userData.lastName,
        cardHolderModel: userData.cardHolderModel,
        birthday: userData.birthday,
        country: userData.country,
        town: userData.town,
        postCode: userData.postCode,
        nationality: userData.nationality,
        gender: userData.gender,
        occupation: userData.occupation,
        annualSalary: userData.annualSalary,
        accountPurpose: userData.accountPurpose,
        expectedMonthlyVolume: userData.expectedMonthlyVolume,
        idType: userData.idType,
        idNumber: userData.idNumber,
        issueDate: userData.issueDate,
        idNoExpiryDate: userData.idNoExpiryDate,
        idFrontId: userData.idFrontId,
        idBackId: userData.idBackId,
        idHoldId: userData.idHoldId,
      };

      const filteredCardHolderData = Object.fromEntries(
        Object.entries(cardHolderData).filter(
          ([, value]) => value !== undefined,
        ),
      );

      await createCardHolder.mutateAsync(
        filteredCardHolderData as TCreateCardHolderProps,
      );
    } catch (error) {
      console.error('Failed to create card holder:', error);
      Sentry.captureException({
        message: 'Failed to create card holder',
        extra: {
          error,
        },
      });
    } finally {
      setIsLoading(false);
      setUserData(undefined);
    }
  }, [userInfo, userData, createCardHolder]);

  /* Effects */
  useEffect(() => {
    if (userInfo?.id && userData) {
      createNewCardHolder();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo, userData]);

  /* Return */
  return {
    isLoading,
    error,
    register,
  };
};
